"use client";

import { create } from "zustand";
import { persist } from "zustand/middleware";

export type AccountKind = "user" | "org";

export type StoreAccount = {
  id: string;
  slug: string;
  kind: AccountKind;
};

type AccountStore = {
  accounts: StoreAccount[];
  currentAccount: StoreAccount | null;
  setAccounts: (accounts: StoreAccount[]) => void;
  addAccount: (account: StoreAccount) => void;
  removeAccount: (id: string) => void;
  clearAccounts: () => void;
  setCurrentAccount: (account: StoreAccount | null) => void;
  getAccountByKind: (kind: AccountKind) => StoreAccount | undefined;
};

export const useAccountStore = create<AccountStore>()(
  persist(
    (set, get) => ({
      accounts: [],
      currentAccount: null,
      setAccounts: (accounts) => set({ accounts }),
      addAccount: (account) =>
        set((state) => {
          const exists = state.accounts.some((a) => a.slug === account.slug);
          if (exists) return state;
          return { accounts: [...state.accounts, account] };
        }),
      removeAccount: (id) =>
        set((state) => ({
          accounts: state.accounts.filter((a) => a.id !== id),
        })),
      clearAccounts: () => {
        set({ accounts: [], currentAccount: null });
        if (typeof document !== "undefined") {
          document.cookie = "account_slug=;path=/;max-age=0";
        }
      },
      setCurrentAccount: (account) => {
        set({ currentAccount: account });
        if (typeof document !== "undefined") {
          if (account?.slug) {
            document.cookie = `account_slug=${account.slug};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
          } else {
            document.cookie = "account_slug=;path=/;max-age=0";
          }
        }
      },
      getAccountByKind: (kind) => get().accounts.find((a) => a.kind === kind),
    }),
    {
      name: "geoplatform-account-storage",
      partialize: (state) => ({
        accounts: state.accounts,
        currentAccount: state.currentAccount,
      }),
    },
  ),
);
